/**
 * Section progress, computed from whatever the survey is currently showing.
 *
 * Kept free of SurveyJS types so it can be reasoned about and tested directly: the runner reduces
 * the live model to these plain shapes and passes them in.
 *
 * Progress counts *answered questions*, not pages. A page-based bar tells a respondent that a
 * page of eleven warehouse fields is worth the same as a page with one yes/no on it.
 */

export interface QuestionSnapshot {
  name: string;
  semanticId?: string;
  visible: boolean;
  required: boolean;
  answered: boolean;
}

export interface PageSnapshot {
  name: string;
  title: string;
  section: string;
  sectionLabel: string;
  sectionWhy: string;
  depth: "essential" | "detail";
  visible: boolean;
  /** Index among the *visible* pages — what navigation uses. -1 when hidden. */
  navIndex: number;
  questions: QuestionSnapshot[];
}

export interface SectionProgress {
  key: string;
  label: string;
  why: string;
  /** Index of the section's first visible page, for navigation. */
  firstPageIndex: number;
  pageCount: number;
  questionCount: number;
  answeredCount: number;
  requiredCount: number;
  requiredAnsweredCount: number;
  /** True when every required question in the section has an answer. */
  complete: boolean;
  started: boolean;
  /** A section made only of detail pages can be skipped without losing the essentials. */
  optional: boolean;
}

export interface SurveyProgress {
  sections: SectionProgress[];
  currentSectionKey: string | undefined;
  answered: number;
  total: number;
  requiredAnswered: number;
  requiredTotal: number;
  percent: number;
  /** Questions the respondent no longer has to answer because of their earlier answers. */
  skippedByBranching: number;
}

export function computeProgress(pages: PageSnapshot[], currentPageIndex: number): SurveyProgress {
  const sections = new Map<string, SectionProgress>();
  let answered = 0;
  let total = 0;
  let requiredAnswered = 0;
  let requiredTotal = 0;
  let skippedByBranching = 0;
  let currentSectionKey: string | undefined;

  pages.forEach((page) => {
    if (!page.visible) {
      skippedByBranching += page.questions.length;
      return;
    }

    let section = sections.get(page.section);
    if (!section) {
      section = {
        key: page.section,
        label: page.sectionLabel,
        why: page.sectionWhy,
        firstPageIndex: page.navIndex,
        pageCount: 0,
        questionCount: 0,
        answeredCount: 0,
        requiredCount: 0,
        requiredAnsweredCount: 0,
        complete: false,
        started: false,
        optional: true,
      };
      sections.set(page.section, section);
    }

    section.pageCount += 1;
    if (page.depth !== "detail") section.optional = false;
    if (page.navIndex === currentPageIndex) currentSectionKey = page.section;

    for (const question of page.questions) {
      if (!question.visible) {
        skippedByBranching += 1;
        continue;
      }
      total += 1;
      section.questionCount += 1;
      if (question.answered) {
        answered += 1;
        section.answeredCount += 1;
      }
      if (question.required) {
        requiredTotal += 1;
        section.requiredCount += 1;
        if (question.answered) {
          requiredAnswered += 1;
          section.requiredAnsweredCount += 1;
        }
      }
    }
  });

  for (const section of sections.values()) {
    section.started = section.answeredCount > 0;
    section.complete =
      section.questionCount > 0 &&
      section.requiredAnsweredCount === section.requiredCount &&
      section.answeredCount > 0;
  }

  return {
    sections: [...sections.values()],
    currentSectionKey,
    answered,
    total,
    requiredAnswered,
    requiredTotal,
    percent: total === 0 ? 0 : Math.round((answered / total) * 100),
    skippedByBranching,
  };
}

/** Upload categories that let the runner soften or hide questions a file already answers. */
export const UPLOAD_FLAGS: Record<string, string> = {
  product_master: "uploaded_product_master",
  warehouse_master: "uploaded_warehouse_master",
  retailer_list: "uploaded_retailer_list",
  inventory_snapshot: "uploaded_inventory_snapshot",
  sop: "uploaded_sop",
  process_document: "uploaded_sop",
  retailer_guide: "uploaded_retailer_guide",
  edi_specification: "uploaded_edi_spec",
  api_documentation: "uploaded_api_docs",
  org_chart: "uploaded_org_chart",
  order_history: "uploaded_order_history",
  carrier_configuration: "uploaded_carrier_config",
};

export interface UploadLike {
  upload_category_code: string;
  processing_status: string;
  original_filename: string;
}

/** Turn an organization's uploads into the SurveyJS variables the library branches on. */
export function uploadFlags(uploads: UploadLike[]): Record<string, boolean> {
  const flags: Record<string, boolean> = {};
  for (const name of Object.values(UPLOAD_FLAGS)) flags[name] = false;
  for (const upload of uploads) {
    // A file we could not read cannot stand in for an answer.
    if (upload.processing_status !== "parsed") continue;
    const flag = UPLOAD_FLAGS[upload.upload_category_code];
    if (flag) flags[flag] = true;
  }
  return flags;
}

/** What to tell the respondent we already learned from their files. */
export function uploadSummary(uploads: UploadLike[]): string[] {
  const parsed = uploads.filter((upload) => upload.processing_status === "parsed");
  const byCategory = new Map<string, string[]>();
  for (const upload of parsed) {
    const list = byCategory.get(upload.upload_category_code) ?? [];
    list.push(upload.original_filename);
    byCategory.set(upload.upload_category_code, list);
  }
  return [...byCategory.entries()].map(
    ([category, files]) => `${category.replace(/_/g, " ")}: ${files.join(", ")}`,
  );
}
